/**
 * CreatePostPage - Create new haircut post (Barbers/Admins only)
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { usePostPermissions } from '@/hooks/usePostPermissions';
import { toast } from 'sonner';
import { X, Camera } from 'lucide-react';
import { getFranchiseIdFromUrl } from '@/config/franchiseUrls';

export default function CreatePostPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, firebaseUser } = useAuth();
  const permissions = usePostPermissions();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState('');
  const [uploading, setUploading] = useState(false);

  // Show loading while checking permissions
  if (permissions.loading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30 mx-auto mb-4"></div>
          <p className="text-ink-muted">{t('feed.checkingPermissions')}</p>
        </div>
      </div>
    );
  }

  // Check if user can post
  if (!permissions.canCreate) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <div className="bg-surface rounded-lg shadow-sm p-8 text-center max-w-md">
          <p className="text-xl font-bold text-ink mb-4">
            ⚠️ {t('feed.accessDenied')}
          </p>
          <p className="text-ink-muted mb-6">
            {t('feed.onlyBarbersCanPost')}
          </p>
          <button
            onClick={() => navigate('/feed')}
            className="bg-info text-white px-6 py-2 rounded-lg hover:bg-info/90 transition"
          >
            {t('feed.goToFeed')}
          </button>
        </div>
      </div>
    );
  }

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error(t('feed.selectImage'));
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('feed.imageTooLarge'));
      return;
    }

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!imageFile || !firebaseUser || !user) {
      toast.error(t('feed.selectAnImage'));
      return;
    }

    setUploading(true);

    try {
      // Upload image to Firebase Storage
      const timestamp = Date.now();
      const fileName = `posts/${firebaseUser.uid}/${timestamp}_${imageFile.name}`;
      const storageRef = ref(storage, fileName);

      await uploadBytes(storageRef, imageFile);
      const imageUrl = await getDownloadURL(storageRef);

      // Parse tags
      const tagList = tags
        .split(' ')
        .filter(tag => tag.trim().length > 0)
        .map(tag => tag.replace('#', '').trim())
        .filter(tag => tag.length > 0);

      // Get franchiseId
      const franchiseId = user.franchiseId || getFranchiseIdFromUrl();

      if (!franchiseId) {
        toast.error(t('feed.couldNotDetermineFranchise'));
        setUploading(false);
        return;
      }

      // Create post
      const postData: any = {
        authorId: firebaseUser.uid,
        authorName: user.displayName || t('feed.barber'),
        authorPhoto: user.photoURL || null,
        authorRole: user.role,
        franchiseId, // CRITICAL: Required for feed filtering
        imageUrl,
        likesCount: 0,
        commentsCount: 0,
        isActive: true,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Add optional fields only if they exist
      if (user.branchId) postData.branchId = user.branchId;
      if (caption.trim()) postData.caption = caption.trim();
      if (tagList.length > 0) postData.tags = tagList;

      await addDoc(collection(db, 'posts'), postData);

      toast.success(t('feed.postPublished'));
      navigate('/feed');
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(t('feed.publishError'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-2 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-ink">
            📸 {t('feed.createPost')}
          </h1>
          <button
            onClick={() => navigate('/feed')}
            className="text-ink-muted hover:text-ink dark:hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Image Upload */}
          <div className="bg-surface rounded-lg shadow-sm p-6">
            <label className="block text-sm font-medium text-ink-muted mb-4">
              {t('feed.haircutPhoto')} *
            </label>

            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full aspect-square object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview('');
                  }}
                  className="absolute top-2 right-2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full aspect-square border-2 border-dashed border-hairline rounded-lg cursor-pointer hover:border-info/30 dark:hover:border-info/30 transition">
                <div className="flex flex-col items-center justify-center py-6">
                  <Camera className="w-12 h-12 text-ink-subtle mb-3" />
                  <p className="text-sm text-ink-muted mb-1">
                    {t('feed.clickToUpload')}
                  </p>
                  <p className="text-xs text-ink-subtle text-ink-muted">
                    {t('feed.imageFormat')}
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Caption */}
          <div className="bg-surface rounded-lg shadow-sm p-6">
            <label className="block text-sm font-medium text-ink-muted mb-2">
              {t('feed.description')}
            </label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={t('feed.writeAboutCut')}
              rows={4}
              className="w-full px-4 py-2 border border-hairline bg-surface text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
            />
          </div>

          {/* Tags */}
          <div className="bg-surface rounded-lg shadow-sm p-6">
            <label className="block text-sm font-medium text-ink-muted mb-2">
              {t('feed.tagsLabel')}
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder={t('feed.tagsPlaceholder')}
              className="w-full px-4 py-2 border border-hairline bg-surface text-ink rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent"
            />
            <p className="text-xs text-ink-subtle mt-2">
              {t('feed.tagsHelp')}
            </p>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/feed')}
              className="flex-1 bg-surface-2 text-ink px-6 py-3 rounded-lg font-semibold hover:bg-surface-3 transition"
            >
              {t('feed.cancel')}
            </button>
            <button
              type="submit"
              disabled={!imageFile || uploading}
              className="flex-1 bg-info text-ink px-6 py-3 rounded-lg font-semibold hover:bg-info/90 disabled:bg-surface-3 disabled:cursor-not-allowed transition"
            >
              {uploading ? t('feed.publishing') : t('feed.publish')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
